import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAnimation } from '../contexts/AnimationContext';
import api, { formatApiError, isAuthenticated, getTenantSubdomain } from '../services/api';
import RecaptchaWidget from './RecaptchaWidget';
import './Login.css';

const ForgotPassword = () => {
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const { useTransformersAnimation } = useAnimation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [emailFocused, setEmailFocused] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [requiresCaptcha, setRequiresCaptcha] = useState(false);
  const [captchaSiteKey, setCaptchaSiteKey] = useState(null);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const emailInputRef = useRef(null);
  const isRsTenant = (getTenantSubdomain() || '').toLowerCase() === 'rs';
  const authAccent = isRsTenant ? '#2a2a2a' : '#0045f5';
  const authFocusShadow = isRsTenant
    ? 'focus:shadow-[0_0_0_1px_rgba(42,42,42,1)]'
    : 'focus:shadow-[0_0_0_1px_rgba(0,69,245,1)]';

  // Redirect authenticated users to admin dashboard
  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/admin-dashboard', { replace: true });
    }
  }, [navigate]);

  const handleEmailFocus = () => {
    setEmailFocused(true);
  };

  const handleEmailBlur = () => {
    setEmailFocused(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (requiresCaptcha && !recaptchaToken) {
      setError(t('completeCaptcha'));
      return;
    }
    setIsLoading(true);
    setError(null);
    setFieldErrors({});
    setIsSubmitted(false);

    try {
      await api.forgotPassword(email, recaptchaToken || undefined);
      // Always show success message (security best practice)
      setIsSubmitted(true);
      setEmail(''); // Clear email after successful submission
    } catch (err) {
      const formattedError = formatApiError(err);
      setFieldErrors(formattedError.fieldErrors);
      setError(formattedError.generalError);
      setRequiresCaptcha(formattedError.requiresCaptcha || false);
      setCaptchaSiteKey(formattedError.captchaSiteKey || null);
      setRecaptchaToken('');
    } finally {
      setIsLoading(false);
    }
  };

  // Focus on email input after animation completes (only for transformers animation)
  useEffect(() => {
    if (useTransformersAnimation) {
      const timer = setTimeout(() => {
        if (emailInputRef.current) {
          emailInputRef.current.focus();
        }
      }, 600); // Wait for form container animation (0.5s) + small buffer

      return () => clearTimeout(timer);
    }
  }, [useTransformersAnimation]);

  // Render form container based on animation mode
  const FormContainer = useTransformersAnimation ? motion.div : 'div';
  const formContainerProps = useTransformersAnimation ? {
    layoutId: "form-container",
    layout: false,
    style: { willChange: 'height' }
  } : {};

  // Render header based on animation mode
  const HeaderContainer = useTransformersAnimation ? motion.div : 'div';
  const headerProps = useTransformersAnimation ? {
    layoutId: "header",
    layout: false,
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.35, ease: 'easeOut' },
    style: { willChange: 'opacity' }
  } : {};

  // Render input wrapper based on animation mode
  const InputWrapper = useTransformersAnimation ? motion.div : 'div';
  const getInputWrapperProps = (fieldName, delay) => useTransformersAnimation ? {
    layoutId: `${fieldName}-input`,
    layout: false,
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.3, delay, ease: 'easeInOut' },
    style: { willChange: 'opacity' }
  } : {};

  // Render button based on animation mode
  const ButtonWrapper = useTransformersAnimation ? motion.button : 'button';
  const buttonProps = useTransformersAnimation ? {
    layoutId: "submit-button",
    layout: false,
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.3, delay: 0.2, ease: 'easeInOut' },
    style: { 
      backgroundColor: '#0045f5',
      willChange: 'opacity' 
    }
  } : {
    style: { backgroundColor: '#0045f5' }
  };

  return (
    <>
      <main className="relative z-10 w-full px-0 py-0 md:px-4 md:py-12 md:max-w-[800px] h-screen md:h-auto">
        <div className="relative p-[2px] rounded-none md:rounded-2xl transition-all duration-500 w-full h-full md:h-auto transform group/card scale-100 md:scale-[0.8]" style={{ backgroundColor: authAccent, transformOrigin: 'center center' }}>
          <FormContainer className="glass-card rounded-none md:rounded-2xl p-6 md:p-10 w-full h-full relative z-10 flex flex-col overflow-y-auto" {...formContainerProps}>
            <HeaderContainer className="text-center mb-2" {...headerProps}>
              <h1 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight mt-8 md:mt-0">{t('forgotTitle')}</h1>
              <p className="text-gray-500 text-base">
                {isSubmitted 
                  ? t('forgotSubmittedSubtitle')
                  : t('forgotSubtitle')}
              </p>
            </HeaderContainer>

            {/* Error message */}
            {error && !isSubmitted && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {!isSubmitted ? (
              <form onSubmit={handleSubmit} className="flex flex-col gap-5 flex-grow">
                {/* Email input */}
                <InputWrapper className="group relative transition-all duration-300" {...getInputWrapperProps('email', 0.05)}>
                  <label 
                    className={`block text-sm font-semibold text-nokia-blue mb-1.5 ml-1 transition-all duration-300 opacity-0 -translate-y-2 group-focus-within:opacity-100 group-focus-within:translate-y-0`} 
                    htmlFor="email"
                  >
                    {tCommon('email')}
                  </label>
                  <div className="relative flex items-center">
                    <span className="material-symbols-outlined absolute left-4 text-gray-400 transition-colors group-focus-within:text-nokia-blue z-10">mail</span>
                    <input
                      ref={emailInputRef}
                      className={`w-full bg-white border-2 ${
                        fieldErrors.email ? 'border-red-500' : 'border-gray-300'
                      } text-gray-900 text-base rounded-lg focus:ring-0 focus:border-nokia-blue block p-4 pl-12 placeholder-gray-400 transition-all duration-300 ease-out hover:border-gray-400 ${authFocusShadow} h-12`}
                      id="email"
                      type="email"
                      placeholder={t('emailPlaceholder')}
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (fieldErrors.email) {
                          setFieldErrors({ ...fieldErrors, email: null });
                        }
                      }}
                      onFocus={handleEmailFocus}
                      onBlur={handleEmailBlur}
                      required
                      disabled={isLoading}
                    />
                  </div>
                  {fieldErrors.email && (
                    <p className="mt-1 text-sm text-red-600 ml-1">{fieldErrors.email[0]}</p>
                  )}
                </InputWrapper>

                {/* reCAPTCHA when required after 5 attempts */}
                {requiresCaptcha && (captchaSiteKey ? (
                  <RecaptchaWidget
                    siteKey={captchaSiteKey}
                    onChange={(token) => setRecaptchaToken(token || '')}
                    theme={document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
                  />
                ) : (
                  <p className="text-amber-600 dark:text-amber-400 text-sm my-2">
                    {t('tooManyAttempts')}
                  </p>
                ))}

                {/* Submit button */}
                <ButtonWrapper
                  className={`relative overflow-hidden w-full text-white font-bold rounded-lg py-3 md:py-4 px-4 md:px-6 mt-6 transition-all duration-300 transform active:scale-[0.98] group border border-transparent ${
                    isLoading ? 'opacity-75 cursor-not-allowed' : ''
                  }`}
                  {...buttonProps}
                  type="submit"
                  disabled={isLoading || (requiresCaptcha && !recaptchaToken)}
                >
                  <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-shimmer"></div>
                  <span className="relative z-10 flex items-center justify-center gap-2 tracking-wide text-sm md:text-base">
                    {t('sendResetLink')}
                  </span>
                </ButtonWrapper>
              </form>
            ) : (
              <div className="flex flex-col items-center gap-4 py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="material-symbols-outlined text-4xl text-green-600">check_circle</span>
                </div>
                <p className="text-center text-gray-600">{t('sentInstructionsTo')}</p>
                <p className="text-center font-semibold text-nokia-blue">{email}</p>
              </div>
            )}

            {/* Back to login */}
            <div className="mt-8 text-center">
              <p className="text-gray-500 text-base">
                {t('rememberPassword')}{' '}
                <Link to="/login" className="text-nokia-blue font-bold hover:text-primary-hover transition-all">
                  {t('signIn')}
                </Link>
              </p>
            </div>
          </FormContainer>

          {/* Decorative corners */}
          <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-nokia-blue rounded-tl-2xl opacity-40 group-hover/card:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
          <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-nokia-green rounded-br-2xl opacity-40 group-hover/card:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
        </div>

      </main>
    </>
  );
};

export default ForgotPassword;
